import { useCallback, useEffect, useState } from 'react';

import { keyframes } from '@chakra-ui/react';
import { Card, CardStatus, Contest, ContestParticipant } from 'models';

import { useChooseCardMutation, useCompleteParticipantMutation } from 'api/contest';

import { setParticipation } from 'store/slices/contest';

import { useAppDispatch } from './app';
import useContest from './useContest';

// const revealAnimation = keyframes`
//   0% { transform: translateX(-100%) rotate(0deg) scale(0); opacity: 0;  }
//   100% { transform: translateX(0) rotate(360deg) scale(1); opacity: 1; }
// `;

const revealAnimation = keyframes`
  0% {
    transform: translate(-200%, 0) rotate(-30deg) scale(1.2);
    // box-shadow: 40px 40px 60px -5px rgba(0,0,0,0.5);

  }
  100% {
    transform: translate(0px, 0px) rotate(0deg) scale(1);
    box-shadow: 0px 10px 10px -5px rgba(0,0,0,1);

  }
`;

const defaultAnimation = keyframes`
  0% {
    transform: translate(0px, 0px) rotate(0deg) scale(1);
  }
  50% {
  }
  100% { 
    transform: translate(20px, 60vh) rotate(10deg) scale(.3);
    opacity: .5;
  } 
`;

const cutAnimation = keyframes`
0% {
    transform: translate(0px, 0px) rotate(0deg) scale(1);
    box-shadow: 0px 10px 10px -5px rgba(0,0,0,1);
  }
  100% {
    transform: translate(200%, 0px) rotate(20deg) scale(.8);
    box-shadow: 40px 40px 60px -5px rgba(0,0,0,0.5);
    opacity: 0.5;
  }
`;

const benchAnimation = keyframes`
 0% {
    transform: translate(0px, 0px)
  }  
  50% {
  }   
  100% {  
    transform: translate(50vw , 60vh) rotate(20deg) scale(0.3);     
    opacity: .5; 
  }
`;

interface UseBuilderProps {
  contest: Contest | null;
  participation: ContestParticipant | null;
}

const useBuilder = ({ contest, participation }: UseBuilderProps) => {
  const dispatch = useAppDispatch();

  const setParticipationState = (participation: ContestParticipant | null) => {
    dispatch(setParticipation(participation));
  };

  const card = participation?.cards.lastCardWithoutStatus;
  const cardsWithStatus = participation?.cards.cardsWithStatus ?? [];
  const selectedCards = cardsWithStatus.filter((card) => card.status === 'start');
  const cutCards = cardsWithStatus.filter((card) => card.status === 'cut');
  const benchCard = cardsWithStatus.find((card) => card.status === 'bench');

  const [isAnimating, setIsAnimating] = useState(false);
  const [animation, setAnimation] = useState<string>(revealAnimation);

  const [chooseCardMutation] = useChooseCardMutation();

  const [
    completeParticipant,
    { error: completeError, isLoading: isCompleteParticipantLoading, isSuccess: isCompleteParticipantSuccess },
  ] = useCompleteParticipantMutation();

  const chooseCard = useCallback(
    async (card: Card, status: CardStatus) => {
      if (!contest || isAnimating) return;
      setIsAnimating(true);
      const animation = (() => {
        if (status === 'cut') return cutAnimation;
        if (status === 'bench') return benchAnimation;
        return defaultAnimation;
      })();
      setAnimation(animation);
      // TODO: refactor
      setTimeout(async () => {
        const { data } = await chooseCardMutation({
          contestId: contest.id,
          cardId: card.id,
          status,
        });
        setParticipationState(data ?? null);
      }, 0);
    },
    [contest]
  );

  useEffect(() => {
    setAnimation(revealAnimation);
    setIsAnimating(false);
  }, [card]);

  const completeSquad = useCallback(async () => {
    if (!participation) return;
    const { data, error } = await completeParticipant({
      participantId: participation.id,
    });
    if (error) {
      console.error(error);
    } else {
      setParticipationState(data);
    }
  }, [participation]);

  return {
    isLoading: isCompleteParticipantLoading,
    isSuccess: isCompleteParticipantSuccess,
    error: completeError ?? null,
    chooseCard,
    completeSquad,
    selectedCards,
    benchCard,
    card,
    cutCards,
    animation,
    cardsWithStatus: cardsWithStatus ?? [],
    isAnimating,
  };
};

export default useBuilder;
